import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { CashAdvanceRequestService } from "services/cash-advance-request";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts) => {
  if (!parts.day || !parts.month || !parts.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

const validateDate = (parts, label) => {
  const date = composeDate(parts);
  if (!date) throw new Error(`${label} chưa hợp lệ`);
  const [d, m, y] = date.split("/").map(Number);
  const maxDay = new Date(y, m, 0).getDate();
  if (m < 1 || m > 12 || y < 1900 || y > 9999 || d < 1 || d > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }
  return date;
};

const parseAmountToNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return 0;
  return Number(digits);
};

const amountToVietnameseWords = (value) => {
  const number = parseAmountToNumber(value);
  if (!number) return "";

  const chuSo = ["không", "một", "hai", "ba", "bốn", "năm", "sáu", "bảy", "tám", "chín"];
  const tien = ["", "nghìn", "triệu", "tỷ", "nghìn tỷ", "triệu tỷ"];
  const strNumber = String(Math.round(number));
  const chunks = [];

  for (let i = strNumber.length; i > 0; i -= 3) {
    chunks.push(strNumber.substring(Math.max(i - 3, 0), i));
  }

  const docHangChuc = (so, daydu) => {
    let chuoi = "";
    const chuc = Math.floor(so / 10);
    const donvi = so % 10;

    if (chuc > 1) {
      chuoi = ` ${chuSo[chuc]} mươi`;
      if (donvi === 1) chuoi += " mốt";
    } else if (chuc === 1) {
      chuoi = " mười";
      if (donvi === 1) chuoi += " một";
    } else if (daydu && donvi > 0) {
      chuoi = " linh";
    }

    if (donvi === 5 && chuc >= 1) chuoi += " lăm";
    else if (donvi > 1 || (donvi === 1 && chuc === 0)) chuoi += ` ${chuSo[donvi]}`;
    return chuoi;
  };

  const docKhoi = (so, daydu) => {
    const tram = Math.floor(so / 100);
    const soDu = so % 100;
    if (daydu || tram > 0) {
      return ` ${chuSo[tram]} trăm${docHangChuc(soDu, true)}`;
    }
    return docHangChuc(soDu, false);
  };

  let ketQua = "";
  for (let i = chunks.length - 1; i >= 0; i -= 1) {
    const soNhom = parseInt(chunks[i], 10);
    if (soNhom > 0) {
      ketQua += `${docKhoi(soNhom, i !== chunks.length - 1)} ${tien[i]}`;
    }
  }

  const normalized = ketQua.trim();
  if (!normalized) return "";
  return `${normalized.charAt(0).toUpperCase() + normalized.slice(1)} đồng`;
};

export default function CashAdvanceRequestDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({ day: "", month: "", year: "" });
  const [form, setForm] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    recipient: "",
    requesterName: "",
    requesterAddress: "",
    amountInNumber: "",
    amountInWords: "",
    reason: "",
    paymentDeadline: "",
  });

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await CashAdvanceRequestService.getById(id);
        const data = response.data ? response.data : response;
        setForm({
          unit: data.unit || "",
          department: data.department || "",
          documentNumber: data.documentNumber || "",
          recipient: data.recipient || "",
          requesterName: data.requesterName || "",
          requesterAddress: data.requesterAddress || "",
          amountInNumber: formatVndIntegerInput(data.amountInNumber || ""),
          amountInWords: amountToVietnameseWords(data.amountInNumber || "") || data.amountInWords || "",
          reason: data.reason || "",
          paymentDeadline: data.paymentDeadline || "",
        });
        setDocumentDateParts(splitDate(data.documentDate || ""));
      } catch (error) {
        toast.error("Không thể tải dữ liệu giấy đề nghị tạm ứng");
      }
    })();
  }, [id]);

  const setField = (key, value) => {
    if (key === "amountInNumber") {
      const formattedValue = formatVndIntegerInput(value);
      setForm((prev) => ({
        ...prev,
        amountInNumber: formattedValue,
        amountInWords: amountToVietnameseWords(formattedValue),
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setDocumentDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setDocumentDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const buildPayload = () => {
    const amountInNumberValue = parseVndIntegerInput(form.amountInNumber || "");
    const payload = {
      ...form,
      documentDate: validateDate(documentDateParts, "Ngày chứng từ"),
      unit: String(form.unit || "").trim(),
      department: String(form.department || "").trim(),
      documentNumber: String(form.documentNumber || "").trim(),
      recipient: String(form.recipient || "").trim(),
      requesterName: String(form.requesterName || "").trim(),
      requesterAddress: String(form.requesterAddress || "").trim(),
      amountInNumber: String(amountInNumberValue),
      amountInWords: String(form.amountInWords || "").trim(),
      reason: String(form.reason || "").trim(),
      paymentDeadline: String(form.paymentDeadline || "").trim(),
    };

    const required = [
      ["unit", "Đơn vị"],
      ["department", "Bộ phận"],
      ["documentNumber", "Số chứng từ"],
      ["recipient", "Kính gửi"],
      ["requesterName", "Tên người đề nghị"],
      ["requesterAddress", "Địa chỉ"],
      ["amountInNumber", "Số tiền tạm ứng (bằng số)"],
      ["amountInWords", "Số tiền tạm ứng (bằng chữ)"],
      ["reason", "Lý do tạm ứng"],
      ["paymentDeadline", "Thời hạn thanh toán"],
    ];

    required.forEach(([key, label]) => {
      if (!payload[key]) {
        throw new Error(`${label} không được để trống`);
      }
    });
    if (amountInNumberValue <= 0) {
      throw new Error("Số tiền tạm ứng (bằng số) phải lớn hơn 0");
    }

    return payload;
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (id) {
        await CashAdvanceRequestService.update(id, payload);
        toast.success("Cập nhật giấy đề nghị tạm ứng thành công");
      } else {
        await CashAdvanceRequestService.create(payload);
        toast.success("Tạo giấy đề nghị tạm ứng thành công");
      }
      navigation("/templates/cash-advance-request");
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Không thể lưu giấy đề nghị tạm ứng");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      data: {
        ...form,
        documentDate: composeDate(documentDateParts),
      },
    });
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const inputStyle = {
    border: "none",
    borderBottom: "1px dotted #000",
    outline: "none",
    fontSize: 16,
    fontFamily: "inherit",
    background: "transparent",
    minWidth: 20,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox
                p={2}
                style={{
                  fontFamily: '"Times New Roman", Times, serif',
                  fontSize: 16,
                  color: "#000",
                  backgroundColor: "#fff",
                  padding: 20,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...inputStyle, width: 220 }}
                        value={form.unit}
                        onChange={(event) => setField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Bộ phận:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...inputStyle, width: 220 }}
                        value={form.department}
                        onChange={(event) => setField("department", event.target.value)}
                      />
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div>
                      <b>Mẫu số 03 - TT</b>
                    </div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>
                      (Kèm theo Thông tư số 99/2025/TT-BTC
                      <br />
                      ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                    </div>
                  </div>
                </div>

                <h2 style={{ textAlign: "center", margin: "8px 0 4px", textTransform: "uppercase", fontWeight: 700 }}>
                  GIẤY ĐỀ NGHỊ TẠM ỨNG
                </h2>

                <div style={{ textAlign: "center", marginBottom: 8 }}>
                  Ngày
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.day}
                    onChange={(event) => setDateField("day", event.target.value)}
                    onBlur={() => padDateField("day")}
                  />
                  tháng
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.month}
                    onChange={(event) => setDateField("month", event.target.value)}
                    onBlur={() => padDateField("month")}
                  />
                  năm
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 48, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.year}
                    onChange={(event) => setDateField("year", event.target.value)}
                  />
                </div>

                <div style={{ textAlign: "right", marginBottom: 10 }}>
                  Số:{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 160 }}
                    value={form.documentNumber}
                    onChange={(event) => setField("documentNumber", event.target.value)}
                  />
                </div>

                <div style={{ marginBottom: 8 }}>
                  <b>Kính gửi:</b>{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: "74%" }}
                    value={form.recipient}
                    onChange={(event) => setField("recipient", event.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  Tên tôi là:{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: "75%" }}
                    value={form.requesterName}
                    onChange={(event) => setField("requesterName", event.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  Địa chỉ:{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: "80%" }}
                    value={form.requesterAddress}
                    onChange={(event) => setField("requesterAddress", event.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  Đề nghị cho tạm ứng số tiền:{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 170, textAlign: "right" }}
                    value={form.amountInNumber}
                    onChange={(event) => setField("amountInNumber", event.target.value)}
                  />{" "}
                  (Viết bằng chữ):{" "}
                  <input
                    disabled={containView}
                    readOnly
                    style={{ ...inputStyle, width: "42%" }}
                    value={form.amountInWords}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  Lý do tạm ứng:{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: "78%" }}
                    value={form.reason}
                    onChange={(event) => setField("reason", event.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  Thời hạn thanh toán:{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: "70%" }}
                    value={form.paymentDeadline}
                    onChange={(event) => setField("paymentDeadline", event.target.value)}
                  />
                </div>

              </SoftBox>

              <SoftBox p={2} display="flex" justifyContent="space-between">
                <SoftButton color="secondary" onClick={() => navigation("/templates/cash-advance-request")}>
                  Trở về
                </SoftButton>
                <div>
                  {containView && (
                    <SoftButton variant="gradient" color="success" onClick={handlePrint} sx={{ mr: 1 }}>
                      In Bảng
                    </SoftButton>
                  )}
                  {!containView && (
                    <SoftButton color="dark" onClick={handleSubmit} disabled={loadingSubmit}>
                      {id ? "Lưu" : "Tạo"}
                    </SoftButton>
                  )}
                </div>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
